import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, Menu, X, ChevronDown, Globe, User, Phone, ExternalLink, MapPin, Search, Heart } from 'lucide-react';
import { Logo } from './Logo';
import { useCart } from '../App';
import { useCurrency } from '../contexts/CurrencyContext';
import { useSettings } from '../contexts/SettingsContext';
import { useWishlist } from '../contexts/WishlistContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Navbar = ({ scrolled: externalScrolled, hideSearchBar = false }) => {
  const navigate = useNavigate();
  const { itemCount, total: cartTotal } = useCart();
  const { wishlistCount } = useWishlist();
  const { currency, changeCurrency, detectedCountry, showCountryNotification, dismissCountryNotification } = useCurrency();
  const { settings } = useSettings();
  
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(externalScrolled ?? false);
  const [currencyOpen, setCurrencyOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  
  const currencyRef = useRef(null);
  const searchRef = useRef(null);
  const suggestionsTimeoutRef = useRef(null);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
      setSearchQuery('');
      setShowSuggestions(false);
    }
  };

  // Fetch search suggestions with debounce
  const fetchSuggestions = async (query) => {
    if (query.length < 2) {
      setSuggestions([]);
      return;
    }
    
    setLoadingSuggestions(true);
    try {
      const [productsRes, recipesRes, blogRes] = await Promise.all([
        axios.get(`${API_URL}/api/products/search?q=${encodeURIComponent(query)}&limit=3`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/recipes?search=${encodeURIComponent(query)}&limit=3`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/blog?search=${encodeURIComponent(query)}&limit=3`).catch(() => ({ data: [] }))
      ]);
      
      const results = [];
      
      // Add products
      (productsRes.data || []).slice(0, 3).forEach(p => {
        results.push({ type: 'product', id: p.id, title: p.name, url: `/products/${p.id}`, image: p.image_url });
      });
      
      // Add recipes
      (recipesRes.data || []).slice(0, 3).forEach(r => {
        results.push({ type: 'recipe', id: r.id, title: r.title, url: `/recipes/${r.slug}`, image: r.image_url });
      });
      
      // Add blog posts
      (blogRes.data || []).slice(0, 2).forEach(b => {
        results.push({ type: 'blog', id: b.id, title: b.title, url: `/blog/${b.slug}`, image: b.featured_image });
      });
      
      setSuggestions(results);
    } catch (e) {
      console.error('Error fetching suggestions:', e);
    } finally {
      setLoadingSuggestions(false);
    }
  };

  const handleSearchInputChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    
    // Debounce the suggestions fetch
    if (suggestionsTimeoutRef.current) {
      clearTimeout(suggestionsTimeoutRef.current);
    }
    
    if (value.trim().length >= 2) {
      setShowSuggestions(true);
      suggestionsTimeoutRef.current = setTimeout(() => {
        fetchSuggestions(value);
      }, 300);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    navigate(suggestion.url);
    setSearchQuery('');
    setShowSuggestions(false);
    setSuggestions([]);
  };

  useEffect(() => {
    if (externalScrolled === undefined) {
      const handleScroll = () => setScrolled(window.scrollY > 50);
      window.addEventListener('scroll', handleScroll);
      return () => window.removeEventListener('scroll', handleScroll);
    }
  }, [externalScrolled]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (currencyRef.current && !currencyRef.current.contains(e.target)) {
        setCurrencyOpen(false);
      }
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <>
      {/* Country Detection Notification */}
      {showCountryNotification && detectedCountry && (
        <div className="bg-[#003456] text-white text-center py-2 px-4 text-sm animate-fade-in">
          <div className="max-w-7xl mx-auto flex items-center justify-center gap-2">
            <MapPin className="h-4 w-4" />
            <span>
              We detected you're in <strong>{detectedCountry}</strong>. Prices shown in <strong>{currency}</strong>.
            </span>
            <button 
              onClick={dismissCountryNotification}
              className="ml-2 text-white/70 hover:text-white"
              aria-label="Dismiss"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
      
      {/* Promotional Banner - Admin Configurable */}
      {settings.promo_banner_enabled && settings.promo_banner_text && (
        <div className="bg-[#fdc31c] text-[#0a0203] text-center py-2 text-sm font-medium">
          <div className="max-w-7xl mx-auto px-6 flex items-center justify-center gap-2">
            <span>{settings.promo_banner_text}</span>
            {settings.promo_banner_link && (
              <Link to={settings.promo_banner_link} className="underline hover:no-underline font-semibold">
                Shop Now →
              </Link>
            )}
          </div>
        </div>
      )}
      
      <nav className={`fixed ${(showCountryNotification && detectedCountry) || (settings.promo_banner_enabled && settings.promo_banner_text) ? (showCountryNotification && detectedCountry && settings.promo_banner_enabled && settings.promo_banner_text ? 'top-[72px]' : 'top-[36px]') : 'top-0'} left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-md' : 'bg-[#003456]'}`}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex">
            {/* Left: Logo - spans both rows */}
            <Link to="/" className="flex-shrink-0 py-2 pr-8 flex items-center" data-testid="nav-logo">
              <Logo size="xl" />
            </Link>
            
            {/* Right side content */}
            <div className="flex-1 flex flex-col">
              {/* Top Row: Centered utility items */}
              <div className="flex items-center justify-center gap-6 py-3">
                {/* Currency Selector */}
                <div className="relative" ref={currencyRef}>
                  <button 
                    onClick={() => setCurrencyOpen(!currencyOpen)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                      scrolled ? 'text-[#0a0203] hover:bg-gray-100' : 'text-white hover:bg-white/10'
                    }`}
                    data-testid="currency-selector"
                  >
                    <Globe className="h-4 w-4" />
                    <span>{currency}</span>
                    <ChevronDown className="h-3 w-3" />
                  </button>
                  
                  {currencyOpen && (
                    <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 bg-white rounded-xl shadow-xl border border-gray-100 py-2 min-w-[180px] z-[100]">
                      <button 
                        onClick={() => { changeCurrency('CAD'); setCurrencyOpen(false); }}
                        className={`w-full px-4 py-2.5 text-left text-gray-700 hover:bg-[#f0f4f8] flex items-center gap-3 ${currency === 'CAD' ? 'bg-[#f0f4f8] font-medium' : ''}`}
                        data-testid="currency-cad"
                      >
                        <span className="text-xl">🇨🇦</span> 
                        <span>Canada (CAD $)</span>
                      </button>
                      <button 
                        onClick={() => { changeCurrency('USD'); setCurrencyOpen(false); }}
                        className={`w-full px-4 py-2.5 text-left text-gray-700 hover:bg-[#f0f4f8] flex items-center gap-3 ${currency === 'USD' ? 'bg-[#f0f4f8] font-medium' : ''}`}
                        data-testid="currency-usd"
                      >
                        <span className="text-xl">🇺🇸</span>
                        <span>USA (USD $)</span>
                      </button>
                    </div>
                  )}
                </div>

                {/* Divider */}
                <span className={`hidden md:block h-5 w-px ${scrolled ? 'bg-gray-300' : 'bg-white/30'}`}></span>

                {/* Phone Number */}
                {settings.phone_number ? (
                  <a 
                    href={`tel:${settings.phone_number.replace(/\D/g, '')}`}
                    className={`hidden md:flex items-center gap-1.5 text-sm font-medium transition-colors ${
                      scrolled ? 'text-[#003456] hover:text-[#001d30]' : 'text-white hover:text-[#fdc31c]'
                    }`}
                    data-testid="nav-phone"
                  >
                    <Phone className="h-4 w-4" />
                    <span>{settings.phone_number}</span>
                  </a>
                ) : (
                  <span className={`hidden md:flex items-center gap-1.5 text-sm ${scrolled ? 'text-gray-400' : 'text-white/50'}`}>
                    <Phone className="h-4 w-4" />
                    <span>1-800-XXX-XXXX</span>
                  </span>
                )}

                {/* Divider */}
                <span className={`hidden md:block h-5 w-px ${scrolled ? 'bg-gray-300' : 'bg-white/30'}`}></span>

                {/* Customer Portal - External Link */}
                <a 
                  href="https://freezedryersupport.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`hidden md:flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    scrolled ? 'text-[#0a0203] hover:bg-gray-100' : 'text-white hover:bg-white/10'
                  }`}
                  data-testid="customer-portal"
                >
                  <User className="h-4 w-4" />
                  <span>Customer Portal</span>
                  <ExternalLink className="h-3 w-3" />
                </a>

                {/* Divider */}
                <span className={`hidden md:block h-5 w-px ${scrolled ? 'bg-gray-300' : 'bg-white/30'}`}></span>

                {/* Wishlist */}
                <Link to="/wishlist" className="relative p-2" data-testid="nav-wishlist">
                  <Heart className={`h-6 w-6 ${scrolled ? 'text-[#0a0203]' : 'text-white'} transition-colors`} />
                  {wishlistCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center">
                      {wishlistCount}
                    </span>
                  )}
                </Link>

                {/* Cart */}
                <Link to="/cart" className="relative flex flex-col items-center" data-testid="nav-cart">
                  <div className="relative p-2">
                    <ShoppingCart className={`h-6 w-6 ${scrolled ? 'text-[#0a0203]' : 'text-white'} transition-colors`} />
                    {itemCount > 0 && (
                      <span className="absolute -top-1 -right-1 bg-[#fdc31c] text-[#0a0203] text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center">
                        {itemCount}
                      </span>
                    )}
                  </div>
                  {cartTotal > 0 && (
                    <span className={`text-xs font-semibold -mt-1 ${scrolled ? 'text-[#003456]' : 'text-[#fdc31c]'} transition-colors`}>
                      {currency === 'CAD' ? 'CA' : ''}${cartTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {currency}
                    </span>
                  )}
                </Link>

                {/* Mobile Menu Button */}
                <button className="md:hidden p-2" onClick={() => setIsOpen(!isOpen)} data-testid="mobile-menu-btn">
                  {isOpen ? <X className={`h-6 w-6 ${scrolled ? 'text-[#0a0203]' : 'text-white'}`} /> : <Menu className={`h-6 w-6 ${scrolled ? 'text-[#0a0203]' : 'text-white'}`} />}
                </button>
              </div>
              
              {/* Bottom Row: Centered navigation links */}
              <div className={`hidden md:flex items-center justify-center gap-8 py-3 border-t ${scrolled ? 'border-gray-200' : 'border-white/10'}`}>
                {/* Products with Dropdown */}
                <div className="relative group">
                  <Link 
                    to="/products" 
                    className={`flex items-center gap-1 font-medium transition-colors ${scrolled ? 'text-[#0a0203] hover:text-[#003456]' : 'text-white/90 hover:text-white'}`}
                    data-testid="nav-products"
                  >
                    Products
                    <ChevronDown className="h-4 w-4" />
                  </Link>
                  <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-56 bg-white rounded-xl shadow-xl border border-gray-100 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-[100]">
                    <div className="py-2">
                      <Link to="/products" className="block px-4 py-2.5 text-gray-700 hover:bg-[#f0f4f8] hover:text-[#003456] font-medium">
                        All Products
                      </Link>
                      <Link to="/products?category=freeze-dryer" className="block px-4 py-2.5 text-gray-700 hover:bg-[#f0f4f8] hover:text-[#003456]">
                        Home Freeze Dryers
                      </Link>
                      <Link to="/products?category=accessory" className="block px-4 py-2.5 text-gray-700 hover:bg-[#f0f4f8] hover:text-[#003456]">
                        Accessories
                      </Link>
                      {/* Build My Freeze Dryer - External Link controlled by admin */}
                      {settings.configurator_url ? (
                        <a 
                          href={settings.configurator_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 px-4 py-2.5 text-gray-700 hover:bg-[#f0f4f8] hover:text-[#003456]"
                        >
                          Build My Freeze Dryer
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      ) : (
                        <Link to="/configurator" className="block px-4 py-2.5 text-gray-700 hover:bg-[#f0f4f8] hover:text-[#003456]">
                          Build My Freeze Dryer
                        </Link>
                      )}
                      <div className="border-t border-gray-100 my-1"></div>
                      <Link to="/commercial" className="block px-4 py-2.5 text-[#003456] hover:bg-[#f0f4f8] font-medium">
                        Business Solutions →
                        <span className="block text-xs text-gray-500 font-normal">For businesses & large operations</span>
                      </Link>
                    </div>
                  </div>
                </div>
                
                <Link to="/compare" className={`font-medium transition-colors ${scrolled ? 'text-[#0a0203] hover:text-[#003456]' : 'text-white/90 hover:text-white'}`} data-testid="nav-compare">
                  Compare
                </Link>
                
                {/* Resources with Dropdown */}
                <div className="relative group">
                  <Link 
                    to="/resources"
                    className={`flex items-center gap-1 font-medium transition-colors cursor-pointer ${scrolled ? 'text-[#0a0203] hover:text-[#003456]' : 'text-white/90 hover:text-white'}`}
                  >
                    Resources
                    <ChevronDown className="h-4 w-4" />
                  </Link>
                  <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-48 bg-white rounded-xl shadow-xl border border-gray-100 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-[100]">
                    <div className="py-2">
                      <Link to="/resources" className="block px-4 py-2.5 text-gray-700 hover:bg-[#f0f4f8] hover:text-[#003456] font-medium">All Resources</Link>
                      <Link to="/blog" className="block px-4 py-2.5 text-gray-700 hover:bg-[#f0f4f8] hover:text-[#003456]">Blog</Link>
                      <Link to="/recipes" className="block px-4 py-2.5 text-gray-700 hover:bg-[#f0f4f8] hover:text-[#003456]">Recipes</Link>
                      <Link to="/documents" className="block px-4 py-2.5 text-gray-700 hover:bg-[#f0f4f8] hover:text-[#003456]">Documents & Manuals</Link>
                      <Link to="/resources/videos" className="block px-4 py-2.5 text-gray-700 hover:bg-[#f0f4f8] hover:text-[#003456]">Video Gallery</Link>
                      <Link to="/resources/faq" className="block px-4 py-2.5 text-gray-700 hover:bg-[#f0f4f8] hover:text-[#003456]">FAQ</Link>
                    </div>
                  </div>
                </div>
                
                {/* Support with Dropdown */}
                <div className="relative group">
                  <Link 
                    to="/support"
                    className={`flex items-center gap-1 font-medium transition-colors cursor-pointer ${scrolled ? 'text-[#0a0203] hover:text-[#003456]' : 'text-white/90 hover:text-white'}`}
                  >
                    Support
                    <ChevronDown className="h-4 w-4" />
                  </Link>
                  <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-48 bg-white rounded-xl shadow-xl border border-gray-100 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-[100]">
                    <div className="py-2">
                      <Link to="/support" className="block px-4 py-2.5 text-gray-700 hover:bg-[#f0f4f8] hover:text-[#003456] font-medium">Support Center</Link>
                      <Link to="/customer-support" className="block px-4 py-2.5 text-gray-700 hover:bg-[#f0f4f8] hover:text-[#003456]">Customer Support</Link>
                      <Link to="/contact" className="block px-4 py-2.5 text-gray-700 hover:bg-[#f0f4f8] hover:text-[#003456]">Contact Us</Link>
                      <Link to="/repair" className="block px-4 py-2.5 text-gray-700 hover:bg-[#f0f4f8] hover:text-[#003456]">Repair Services</Link>
                      <Link to="/track-order" className="block px-4 py-2.5 text-gray-700 hover:bg-[#f0f4f8] hover:text-[#003456]">Track Order</Link>
                      <Link to="/warranty" className="block px-4 py-2.5 text-gray-700 hover:bg-[#f0f4f8] hover:text-[#003456]">Warranty</Link>
                      <Link to="/accessibility" className="block px-4 py-2.5 text-gray-700 hover:bg-[#f0f4f8] hover:text-[#003456]">Accessibility</Link>
                    </div>
                  </div>
                </div>

                {/* Programs with Dropdown */}
                <div className="relative group">
                  <span className={`flex items-center gap-1 font-medium transition-colors cursor-pointer ${scrolled ? 'text-[#0a0203] hover:text-[#003456]' : 'text-white/90 hover:text-white'}`}>
                    Programs
                    <ChevronDown className="h-4 w-4" />
                  </span>
                  <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-56 bg-white rounded-xl shadow-xl border border-gray-100 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-[100]">
                    <div className="py-2">
                      <Link to="/refer-a-friend" className="block px-4 py-2.5 text-gray-700 hover:bg-[#f0f4f8] hover:text-[#003456]">Refer a Friend</Link>
                      <Link to="/affiliate-program" className="block px-4 py-2.5 text-gray-700 hover:bg-[#f0f4f8] hover:text-[#003456]">Affiliate Program</Link>
                      <Link to="/wholesale" className="block px-4 py-2.5 text-gray-700 hover:bg-[#f0f4f8] hover:text-[#003456]">Wholesale Purchasing</Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Search Bar Section - Appears on all pages */}
        {!hideSearchBar && (
          <div className={`border-t ${scrolled ? 'border-gray-200 bg-white' : 'border-white/10 bg-[#003456]'}`}>
            <div className="max-w-7xl mx-auto px-6 py-2">
              <div className="flex items-center justify-between">
                <div className="flex-1 max-w-xl relative" ref={searchRef}>
                  <form onSubmit={handleSearch}>
                    <div className="relative">
                      <Search className={`absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 ${scrolled ? 'text-gray-400' : 'text-white/50'}`} />
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={handleSearchInputChange}
                        onFocus={() => searchQuery.length >= 2 && setShowSuggestions(true)}
                        placeholder="Search freeze dryers, accessories, recipes..."
                        className={`w-full pl-10 pr-4 py-2 rounded-full text-sm ${
                          scrolled 
                            ? 'bg-gray-100 border-gray-200 text-[#0a0203] placeholder-gray-400' 
                            : 'bg-white/10 border-white/20 text-white placeholder-white/50'
                        } border focus:outline-none focus:ring-2 focus:ring-[#fdc31c]/50`}
                        data-testid="navbar-search"
                      />
                    </div>
                  </form>
                  
                  {/* Auto-suggest Dropdown */}
                  {showSuggestions && (
                    <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden z-[100]">
                      {loadingSuggestions ? (
                        <div className="p-4 text-center text-gray-500 text-sm">
                          <div className="animate-spin h-5 w-5 border-2 border-[#003456] border-t-transparent rounded-full mx-auto mb-2"></div>
                          Searching...
                        </div>
                      ) : suggestions.length > 0 ? (
                        <div className="py-2">
                          {suggestions.map((suggestion, idx) => (
                            <button
                              key={`${suggestion.type}-${suggestion.id}`}
                              onClick={() => handleSuggestionClick(suggestion)}
                              className="w-full px-4 py-2.5 flex items-center gap-3 hover:bg-[#f0f4f8] transition-colors text-left"
                              data-testid={`suggestion-${idx}`}
                            >
                              {suggestion.image && (
                                <img 
                                  src={suggestion.image} 
                                  alt="" 
                                  className="w-10 h-10 rounded-lg object-cover flex-shrink-0"
                                />
                              )}
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-[#0a0203] truncate">{suggestion.title}</p>
                                <p className="text-xs text-gray-500 capitalize">{suggestion.type}</p>
                              </div>
                            </button>
                          ))}
                          <div className="border-t border-gray-100 mt-2 pt-2 px-4 pb-2">
                            <button
                              onClick={handleSearch}
                              className="text-sm text-[#003456] font-medium hover:underline w-full text-left"
                              data-testid="view-all-results"
                            >
                              View all results for "{searchQuery}" →
                            </button>
                          </div>
                        </div>
                      ) : searchQuery.length >= 2 ? (
                        <div className="p-4 text-center text-gray-500 text-sm">
                          No results found for "{searchQuery}"
                        </div>
                      ) : null}
                    </div>
                  )}
                </div>
                <div className="hidden lg:flex items-center gap-6 ml-6">
                  <Link to="/products?category=freeze-dryer" className={`text-sm font-medium transition-colors ${scrolled ? 'text-gray-600 hover:text-[#003456]' : 'text-white/70 hover:text-white'}`}>Freeze Dryers</Link>
                  <Link to="/products?category=accessory" className={`text-sm font-medium transition-colors ${scrolled ? 'text-gray-600 hover:text-[#003456]' : 'text-white/70 hover:text-white'}`}>Accessories</Link>
                  <Link to="/financing" className={`text-sm font-medium transition-colors ${scrolled ? 'text-gray-600 hover:text-[#003456]' : 'text-white/70 hover:text-white'}`}>Financing</Link>
                  <Link to="/track-order" className={`text-sm font-medium transition-colors ${scrolled ? 'text-gray-600 hover:text-[#003456]' : 'text-white/70 hover:text-white'}`}>Track Order</Link>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Mobile Nav */}
        {isOpen && (
          <div className="md:hidden bg-white border-t border-gray-100 shadow-lg max-h-[70vh] overflow-y-auto">
            <div className="px-6 py-4">
              {/* Mobile Links */}
              <div className="flex flex-col gap-3">
                <Link to="/products" className="text-[#0a0203] font-medium py-2" onClick={() => setIsOpen(false)}>Products</Link>
                <Link to="/compare" className="text-[#0a0203] font-medium py-2" onClick={() => setIsOpen(false)}>Compare</Link>
                <Link to="/financing" className="text-[#0a0203] font-medium py-2" onClick={() => setIsOpen(false)}>Financing</Link>
                
                <div className="border-t border-gray-100 pt-2 mt-1">
                  <p className="text-xs text-gray-400 uppercase tracking-wide mb-2">Resources</p>
                  <Link to="/blog" className="text-[#0a0203] font-medium py-2 block" onClick={() => setIsOpen(false)}>Blog</Link>
                  <Link to="/recipes" className="text-[#0a0203] font-medium py-2 block" onClick={() => setIsOpen(false)}>Recipes</Link>
                  <Link to="/documents" className="text-[#0a0203] font-medium py-2 block" onClick={() => setIsOpen(false)}>Documents & Manuals</Link>
                  <Link to="/resources/videos" className="text-[#0a0203] font-medium py-2 block" onClick={() => setIsOpen(false)}>Video Gallery</Link>
                  <Link to="/resources/faq" className="text-[#0a0203] font-medium py-2 block" onClick={() => setIsOpen(false)}>FAQ</Link>
                </div>
                
                <div className="border-t border-gray-100 pt-2 mt-1">
                  <p className="text-xs text-gray-400 uppercase tracking-wide mb-2">Support</p>
                  <Link to="/customer-support" className="text-[#0a0203] font-medium py-2 block" onClick={() => setIsOpen(false)}>Customer Support</Link>
                  <Link to="/contact" className="text-[#0a0203] font-medium py-2 block" onClick={() => setIsOpen(false)}>Contact Us</Link>
                  <Link to="/repair" className="text-[#0a0203] font-medium py-2 block" onClick={() => setIsOpen(false)}>Repair Services</Link>
                  <Link to="/track-order" className="text-[#0a0203] font-medium py-2 block" onClick={() => setIsOpen(false)}>Track Order</Link>
                  <Link to="/warranty" className="text-[#0a0203] font-medium py-2 block" onClick={() => setIsOpen(false)}>Warranty</Link>
                </div>
                
                <div className="border-t border-gray-100 pt-2 mt-1">
                  <p className="text-xs text-gray-400 uppercase tracking-wide mb-2">Programs</p>
                  <Link to="/refer-a-friend" className="text-[#0a0203] font-medium py-2 block" onClick={() => setIsOpen(false)}>Refer a Friend</Link>
                  <Link to="/affiliate-program" className="text-[#0a0203] font-medium py-2 block" onClick={() => setIsOpen(false)}>Affiliate Program</Link>
                  <Link to="/wholesale" className="text-[#0a0203] font-medium py-2 block" onClick={() => setIsOpen(false)}>Wholesale Purchasing</Link>
                </div>
                
                {/* Mobile Customer Portal */}
                <div className="border-t border-gray-100 pt-2 mt-1">
                  <a 
                    href="https://freezedryersupport.com" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="flex items-center gap-2 text-[#003456] font-medium py-2"
                  >
                    <User className="h-4 w-4" />
                    Customer Portal
                    <ExternalLink className="h-3 w-3" />
                  </a>
                  
                  {/* Mobile Phone */}
                  {settings.phone_number && (
                    <a href={`tel:${settings.phone_number.replace(/\D/g, '')}`} className="flex items-center gap-2 text-[#003456] font-medium py-2">
                      <Phone className="h-4 w-4" />
                      {settings.phone_number}
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </nav>
    </>
  );
};

export default Navbar;
